import classNames from 'classnames';
import styles from './ConcernDefinition.module.css';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Dropdown,
  MenuProps,
  Space,
  CheckboxProps,
  Checkbox,
  List,
  Spin,
} from 'antd';
import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import Modal from '../../Modal';
import Icon from 'src/components/Icon/Icon';
import type { FormInstance } from 'antd/es/form';
import { MODAL_STATUS } from 'src/app/constants';
import { getSearchResultFromQueryParameter } from 'src/app/query.service';

interface FormData {
  [key: string]: any;
}

interface ConcernDefinitionProps {
  isActive: boolean;
  onBack: () => void;
  propData: FormData;
  form: FormInstance;
  queryType: string;
  handleNext: (newData: { [key: string]: unknown }) => void;
}
interface ConcernName {
  id: string;
  concernName: string;
}

const ConcernDefinition = forwardRef<any, ConcernDefinitionProps>(
  ({ isActive, onBack, propData, form, queryType, handleNext }, ref) => {
    const [openModal, setOpenModal] = useState(false);
    const [inputPlaceholder, setInputPlaceholder] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isSmallText, setIsSmallText] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<ConcernName[]>([]);
    const [tmpdata, setTmpData] = useState<{ [key: string]: any }>({});
    const [searchValue, setSearchValue] = useState<string>('');
    const inputRef = useRef(null);
    const [selectedValues, setSelectedValues] = useState<{ [key: string]: any }>({
      concernName: null,
    });
    const checkBoxOnChange: CheckboxProps['onChange'] = (e) => {};

    useImperativeHandle(ref, () => ({
      reset() {
        setSelectedValues({
          concernName: null,
        });
      },
    }));

    useEffect(() => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        ...propData,
      }));
    }, [propData]);
    const onFinish = (values: any) => {};
    const applyConcern = () => {
      setOpenModal(false);
      setTmpData({});
      handleNext({ concernName: selectedValues.concernName });
      form.setFieldValue('concernName', selectedValues.concernName?.join(', '));
    };
    const handleCancel = () => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        concernName: [...(tmpdata.concernName || [])],
      }));

      setOpenModal(false);
      setTmpData({});
    };
    const handleInputClick = async (placeholder: string) => {
      setInputPlaceholder(placeholder);
      setOpenModal(true);
      setTmpData({ [placeholder]: selectedValues[placeholder] });

      try {
        const searchParameter = 'concernName';

        setIsLoading(true);
        const concernName: ConcernName[] = await getSearchResultFromQueryParameter<ConcernName>(
          queryType,
          searchParameter,
          selectedValues
        );
        setResult(concernName);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error('Error fetching data:', error);
      }
    };

    const handleModalInputFocus = () => {
      setIsInputFocused(true);
      setIsSmallText(true);
    };

    const handleModalInputBlur = () => {
      if (searchValue.length <= 0) {
        setIsInputFocused(false);
        setIsSmallText(false);
      }
    };

    const onLinkClick = (value: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        concernName: prevValues.concernName?.includes(value)
          ? prevValues.concernName
          : [...(prevValues.concernName || []), value],
      }));
    };
    const handleRemove = (itemRemove: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        concernName: prevValues.concernName?.filter((item: string) => item !== itemRemove),
      }));
    };

    const filteredResult = result.filter((item) =>
      item.concernName?.toLowerCase().includes(searchValue.toLowerCase())
    );

    const handleInputClear = (type: string) => {
      form.setFieldValue(type, ``);
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: [],
      }));
    };

    const handlePlaceholderClick = () => {
      // Focus the input when the placeholder is clicked
      inputRef.current.focus();
    };

    const shouldShowResetButton = ['concernName'].some((key) => {
      const value = selectedValues[key];
      return Array.isArray(value) ? value.length > 0 : value !== null;
    });
    const handleReset = () => {
      form.setFieldValue('concernName', '');
      setSelectedValues({
        concernName: null,
      });
    };
    const moreItems: MenuProps['items'] = [
      {
        key: '1',
        label: '5',
      },
      {
        key: '2',
        label: '10',
      },
      {
        key: '3',
        label: '15',
      },
      {
        key: '4',
        label: '25',
      },
      {
        key: '4',
        label: '50',
      },
      {
        key: '4',
        label: '70',
      },
    ];

    return (
      <div className={classNames(styles.ConcernDefinition)}>
        <div
          className={`
         ${styles['ConcernDefinition--QueryTitle']} 
         ${isActive ? styles['ConcernDefinition--active'] : styles['ConcernDefinition--InActive']}
         `}
          // style={{ backgroundColor: isActive ? '#000000' : '#dcdcdc' }}
        >
          Concern Definition
          {shouldShowResetButton && (
            <div style={{ display: 'inline', float: 'right', marginRight: '30px' }}>
              <Button
                className={`${
                  isActive
                    ? styles['ConcernDefinition--Reset']
                    : styles['ConcernDefinition--InActive']
                }`}
                htmlType="submit"
                onClick={handleReset}
                disabled={!isActive}
              >
                Reset
              </Button>
            </div>
          )}
        </div>
        <div className={classNames(styles['ConcernDefinition--FormContainer'])}>
          <Form
            form={form}
            name="concern_definition_data_form"
            onFinish={onFinish}
            layout="horizontal"
            disabled={!isActive}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="concernName"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('concernName')}
                    placeholder="Concern Name"
                    suffix={
                      selectedValues.concernName?.length > 0 && (
                        <span onClick={() => handleInputClear('concernName')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.concernName?.length > 0 && (
                  <span
                    className={`${classNames(styles['ConcernDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Concern Name ({selectedValues.concernName?.length})
                  </span>
                )}
              </Col>
            </Row>

            {isActive && (
              <Row justify="space-between">
                <Col>
                  <Form.Item>
                    <Button
                      className={classNames(styles['ConcernDefinition--Buttons'])}
                      type="primary"
                      onClick={onBack}
                    >
                      Back
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Modal open={openModal} onClose={handleCancel} width={900} footer={null}>
              <div className={classNames(styles['ConcernDefinition--ModalHeader'])}>
                Select {inputPlaceholder}
                {inputPlaceholder === 'Status' && <span> - {MODAL_STATUS}</span>}
              </div>
              <div className={classNames(styles['ConcernDefinition--ModelContent'])}>
                <div>
                  <p className={classNames(styles['ConcernDefinition--TagTitle'])}>Selection</p>
                  <div className={classNames(styles['ConcernDefinition--TagsContainer'])}>
                    {(selectedValues.concernName || []).map((item: string, index: number) => {
                      return (
                        <Button
                          type="primary"
                          key={`${item}${index}`}
                          onClick={() => handleRemove(item)}
                          className={styles['selected-button']}
                        >
                          {item}
                          <Icon
                            name="close"
                            className={`${styles['ConcernDefinition--ModalDropDownIcon']} ${styles['close-icon']}`}
                          />
                        </Button>
                      );
                    })}
                  </div>
                </div>
                <div className={styles['ConcernDefinition--Search']}>
                  <Row gutter={16} justify="space-between">
                    <Col span={8}>
                      <div style={{ position: 'relative' }}>
                        <Input
                          name="searchFiled"
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          onFocus={handleModalInputFocus}
                          onBlur={handleModalInputBlur}
                          ref={inputRef} // Use ref to access the input
                        />
                        <span
                          className={`${styles['ConcernDefinition--ModalInputText']} ${
                            isSmallText ? styles.small : ''
                          }`}
                          onClick={handlePlaceholderClick} // Click handler to focus the input
                        >
                          Look for {inputPlaceholder}
                        </span>
                      </div>
                    </Col>
                    <Col span={4}>
                      <Dropdown menu={{ items: moreItems }} trigger={['click']}>
                        <Button className={styles['ConcernDefinition--ModalDropDown']}>
                          Limit
                          <Icon
                            name="arrow_drop_down"
                            className={styles['ConcernDefinition--ModalDropDownIcon']}
                          />
                        </Button>
                      </Dropdown>
                    </Col>
                  </Row>
                </div>
                <div className={styles['ConcernDefinition--SearchList']}>
                  <List
                    itemLayout="horizontal"
                    // loading={loading}
                    loading={{
                      spinning: isLoading,
                      indicator: <Spin className={styles['loader-color']} />,
                    }}
                    dataSource={filteredResult}
                    renderItem={(item) => (
                      <List.Item
                        className={`${
                          selectedValues.concernName?.includes(item.concernName)
                            ? styles['selected-item']
                            : styles['list-item']
                        }`}
                        onClick={() => onLinkClick(item.concernName)}
                      >
                        <span>{item.concernName}</span>
                      </List.Item>
                    )}
                  />
                </div>
                <div className={styles['ConcernDefinition--ModalPageNo']}> 1 - 4 / 4 </div>
              </div>
              <div className={styles['ConcernDefinition--ModalFooter']}>
                <Space>
                  <Button
                    onClick={handleCancel}
                    className={styles['ConcernDefinition--CancelButton']}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => applyConcern()}
                    className={styles['ConcernDefinition--Buttons']}
                  >
                    Apply
                  </Button>
                </Space>
              </div>
            </Modal>
          </Form>
        </div>
      </div>
    );
  }
);
ConcernDefinition.displayName = 'ConcernDefinition';
export default ConcernDefinition;
